/** One bot task: what was asked, the bot's progress and report, any changes
 * waiting for approval, a follow-up box once it has finished, and Stop while
 * it is working. Every action returns the fresh task to the caller. */
import { useState } from 'react';
import { toast } from 'sonner';
import { File as FileIcon, FileSpreadsheet, FileText, Table as Table2 } from '@/shared/ui/icons';
import { Button } from '../../../shared/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../shared/ui/collapsible';
import { Textarea } from '../../../shared/ui/textarea';
import { AIResponseMarkdown } from '../../../shared/ui/ai-response-markdown';
import { cn } from '../../../shared/ui/utils';
import { botTasksService, isBotTaskActive, type BotTask, type BotTaskFile, type BotTaskOutcome, type BotTaskStatus } from '../../../services/BotTasksService';
import { checkRoundUnderWay } from './deliverable';
import { downloadFromSignedUrl } from './botFileDownload';

const STATUS_LABEL: Record<BotTaskStatus, string> = {
  starting: 'Starting', running: 'Working', needs_approval: 'Needs your approval', waiting: 'Waiting',
  completed: 'Done', failed: 'Stopped with a problem', cancelled: 'Stopped',
};
const STATUS_TONE: Record<BotTaskStatus, string> = {
  starting: 'text-muted-foreground', running: 'text-muted-foreground', waiting: 'text-muted-foreground',
  needs_approval: 'text-status-warning-foreground', completed: 'text-status-success-foreground',
  failed: 'text-destructive', cancelled: 'text-muted-foreground',
};

const GLYPHS: Record<string, { Icon: typeof FileIcon; kind: string }> = {
  xlsx: { Icon: FileSpreadsheet, kind: 'spreadsheet' }, xls: { Icon: FileSpreadsheet, kind: 'spreadsheet' },
  csv: { Icon: Table2, kind: 'table' }, tsv: { Icon: Table2, kind: 'table' },
  pdf: { Icon: FileText, kind: 'pdf' },
  doc: { Icon: FileText, kind: 'document' }, docx: { Icon: FileText, kind: 'document' }, txt: { Icon: FileText, kind: 'document' }, md: { Icon: FileText, kind: 'document' },
};

function splitName(name: string): [string, string] {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
}

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function BotTaskFiles({ taskId, files }: { taskId: string; files: BotTaskFile[] }) {
  const [downloading, setDownloading] = useState<string | null>(null);
  const stored = files.filter((file) => file.status === 'stored');
  const skipped = files.some((file) => file.status === 'skipped');
  const expired = files.some((file) => file.status === 'expired');
  if (!stored.length && !skipped && !expired) return null;

  const download = async (file: BotTaskFile) => {
    if (downloading) return;
    setDownloading(file.id);
    try {
      const url = await botTasksService.fileUrl(taskId, file.id);
      await downloadFromSignedUrl(url, file.name, file.mimeType);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The file could not be downloaded.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section aria-label="Files" className="space-y-1">
      {stored.length > 0 && <p className="text-xs font-medium text-muted-foreground">Files</p>}
      {stored.map((file) => {
        const [stem, ext] = splitName(file.name);
        const { Icon, kind } = GLYPHS[ext.slice(1).toLowerCase()] ?? { Icon: FileIcon, kind: 'file' };
        return (
          <div key={file.id} className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2">
            <Icon aria-hidden data-glyph={kind} className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex min-w-0 flex-1 text-sm" title={file.name}>
              <span className="truncate">{stem}</span>
              <span className="shrink-0">{ext}</span>
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">{formatSize(file.sizeBytes)}</span>
            <Button size="sm" variant="outline" aria-label={`Download ${file.name}`} disabled={downloading !== null} onClick={() => void download(file)}>
              {downloading === file.id ? 'Downloading' : 'Download'}
            </Button>
          </div>
        );
      })}
      {skipped && <p className="text-xs text-muted-foreground">A file could not be attached</p>}
      {expired && <p className="text-xs text-muted-foreground">This file is no longer available</p>}
    </section>
  );
}

const KNOWN_RESULTS = ['satisfied', 'needs_revision', 'max_iterations_reached', 'failed', 'interrupted', 'budget_reached'];

/** The grader's verdict on the bot's file. An unknown result shows nothing. */
function BotTaskCheck({ outcome, active }: { outcome: BotTaskOutcome; active: boolean }) {
  if (!KNOWN_RESULTS.includes(outcome.result)) return null;
  const line = active ? null : outcome.result === 'satisfied' ? 'Checked: passed' : outcome.note;
  const why = outcome.explanation.trim();
  if (!line && !why) return null;
  return (
    <Collapsible className="space-y-1">
      <div className="flex items-center gap-2 text-sm">
        {line && <p className="min-w-0 break-words">{line}</p>}
        {why && <CollapsibleTrigger className="shrink-0 text-xs text-muted-foreground underline underline-offset-2">Why</CollapsibleTrigger>}
      </div>
      {why && <CollapsibleContent><pre className="whitespace-pre-wrap break-words rounded-lg bg-muted/40 px-3 py-2 font-apple text-xs">{outcome.explanation}</pre></CollapsibleContent>}
    </Collapsible>
  );
}

export function BotTaskCard({ task, onChange, compact = false }: { task: BotTask; onChange: (task: BotTask) => void; compact?: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [followUp, setFollowUp] = useState('');
  const [open, setOpen] = useState(!compact || task.status === 'needs_approval');
  const active = isBotTaskActive(task);

  const act = async (key: string, action: () => Promise<BotTask>) => {
    setBusy(key);
    try { onChange(await action()); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'The task could not be updated.'); }
    finally { setBusy(null); }
  };

  // The first turn is the prompt, already shown as the title.
  const turns = task.messages.slice(task.messages[0]?.role === 'person' ? 1 : 0);
  return (
    <article className="rounded-xl border border-separator bg-card px-4 py-3 font-apple" aria-label={`Task: ${task.prompt.slice(0, 80)}`}>
      <button type="button" className="flex w-full items-start gap-3 text-left" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <div className="min-w-0 flex-1">
          <p className={cn('text-sm font-medium break-words', !open && 'line-clamp-2')}>{task.prompt}</p>
          <p className={cn('mt-0.5 text-xs', STATUS_TONE[task.status])}>
            {active && task.status !== 'needs_approval' && <span aria-hidden className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current align-middle" />}
            {STATUS_LABEL[task.status]}{task.source === 'schedule' ? ' · Scheduled run' : ''}{task.botName && compact ? ` · ${task.botName}` : ''}
            {task.status === 'running' && task.outcome?.result === 'needs_revision' && ` · Checking the file, round ${checkRoundUnderWay(task.outcome.iterations)} of 2`}
            {active && typeof task.parts === 'number' && task.parts > 0 && ` · Working in ${task.parts} ${task.parts === 1 ? 'part' : 'parts'}`}
          </p>
        </div>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {turns.map((turn, index) => (
            <div key={index} className={cn('text-sm', turn.role === 'person' && 'rounded-lg bg-muted/40 px-3 py-2')}>
              {turn.role === 'person' ? <p className="whitespace-pre-wrap break-words">{turn.text}</p> : <AIResponseMarkdown text={turn.text} />}
            </div>
          ))}
          {!turns.length && active && <p className="text-sm text-muted-foreground">Working on it…</p>}

          {task.pendingActions.length > 0 && (
            <section aria-label="Changes waiting for your approval" className="space-y-2 rounded-lg border border-status-warning-border bg-status-warning-bg px-3 py-2">
              <p className="text-xs font-medium">{task.botName ?? 'The bot'} wants to:</p>
              {task.pendingActions.map((action) => (
                <div key={action.toolUseId} className="flex flex-wrap items-center gap-2">
                  <p className="min-w-0 flex-1 text-sm break-words">{action.summary}</p>
                  <Button size="sm" disabled={busy !== null} onClick={() => void act(`approve:${action.toolUseId}`, () => botTasksService.decide(task.id, action.toolUseId, true))}>
                    {busy === `approve:${action.toolUseId}` ? 'Approving' : 'Approve'}
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void act(`decline:${action.toolUseId}`, () => botTasksService.decide(task.id, action.toolUseId, false))}>
                    Decline
                  </Button>
                </div>
              ))}
            </section>
          )}

          {task.files && task.files.length > 0 && <BotTaskFiles taskId={task.id} files={task.files} />}

          {!task.outcome && !active && task.outcomeStale === true && <p className="text-xs text-muted-foreground">Not re-checked after your follow-up.</p>}
          {task.outcome && <BotTaskCheck outcome={task.outcome} active={active} />}

          {task.error && <p role="alert" className="text-sm text-destructive">{task.error}</p>}

          {task.status === 'completed' && (
            <form className="flex items-end gap-2" onSubmit={(event) => {
              event.preventDefault();
              const text = followUp.trim();
              if (text) void act('message', async () => {
                try {
                  const fresh = await botTasksService.message(task.id, text);
                  setFollowUp('');
                  return fresh;
                } catch (err) {
                  // A follow-up whose reply was lost may still have gone
                  // through: show the task as it now is, so it is not sent twice.
                  const current = await botTasksService.get(task.id, { sync: true }).catch(() => null);
                  // It went through if the task is working again or its
                  // transcript has grown (the turn may already have finished).
                  if (current && (current.status !== 'completed' || current.messages.length > task.messages.length)) { setFollowUp(''); return current; }
                  throw err;
                }
              });
            }}>
              <Textarea rows={1} value={followUp} onChange={(event) => setFollowUp(event.target.value)} placeholder="Ask a follow-up" aria-label="Follow-up for the bot" className="min-h-9 text-base lg:text-sm" />
              <Button type="submit" size="sm" disabled={!followUp.trim() || busy !== null}>Send</Button>
            </form>
          )}

          {active && (
            <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={busy !== null} onClick={() => void act('cancel', () => botTasksService.cancel(task.id))}>
              {busy === 'cancel' ? 'Stopping' : 'Stop'}
            </Button>
          )}
        </div>
      )}
    </article>
  );
}
